import React from 'react';
import { Typography } from 'antd';
import ChatTimeline from '../../ChatTimeline.jsx';
import { EmptyTimeline } from '../../timeline/components/EmptyTimeline.jsx';
import ChatWorkspacePanelCoordinator from './ChatWorkspacePanelCoordinator.jsx';
import RecommendationActionStrip from './RecommendationActionStrip.jsx';
import {
  ChatWorkspaceConversationLayout,
  ChatWorkspaceSectionShell,
} from '../layout/index.js';
import { COMPACT_LAYOUT_QUERY } from '../layout/useCompactWorkspaceLayout.js';

const { Text } = Typography;
const COMPOSER_INPUT_SELECTOR = '.flare-composer-input';

function conditionalValue(condition, whenTrue, whenFalse = null) {
  return condition ? whenTrue : whenFalse;
}

function firstTruthy(...values) {
  return values.find(Boolean);
}

export function focusComposerInputAfterPromptClick() {
  if (typeof document === 'undefined') {
    return;
  }
  const focusComposerInput = () => {
    const input = document.querySelector(COMPOSER_INPUT_SELECTOR);
    input?.focus?.();
  };
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(focusComposerInput);
    return;
  }
  focusComposerInput();
}

/**
 * Pure presentation pane for chat timeline + recommendation strip + canvas coordinator.
 */
export default function ChatWorkspaceConversationPane({
  assistantName,
  themeTokens,
  isCompactLayout,
  resolvedActiveModeKey,
  canvasWorkspaceFullscreenMode,
  showCanvasPanel,
  onCanvasPanelClose,
  canvasPanelNode,
  composerNode,
  contentMaxWidth,
  streamError,
  streamLoading,
  messageWindow,
  onLoadOlderMessages,
  onStreamRetry,
  renderedTimeline,
  onUICardAction,
  onWorkflowOperationAction,
  registry,
  skillProjection,
  resolvedUILabels,
  showWorkspaceDebugPanel,
  resolvedDevMode,
  workspaceEventLog,
  workspaceDocSnapshot,
  shouldRenderRecommendationPanel,
  currentInputValue,
  recommendationPanelTitle,
  canExpandScenarios,
  showAllScenarios,
  onToggleShowAllScenarios,
  visibleScenarios,
  onScenarioCardClick,
} = { currentInputValue: '' }) {
  const labels = Object.assign({}, resolvedUILabels);
  const [isNarrowViewport, setIsNarrowViewport] = React.useState(false);
  const [latestUnreadVisible, setLatestUnreadVisible] = React.useState(false);
  const [latestScrollSignal, setLatestScrollSignal] = React.useState(0);
  const [recommendationPanelDismissed, setRecommendationPanelDismissed] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mediaQuery = window.matchMedia(COMPACT_LAYOUT_QUERY);
    const handleChange = (event) => setIsNarrowViewport(Boolean(event.matches));
    setIsNarrowViewport(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);
  React.useEffect(() => {
    if (!shouldRenderRecommendationPanel) {
      setRecommendationPanelDismissed(false);
    }
  }, [shouldRenderRecommendationPanel]);
  const shouldUseCompactRecommendationPanel = [isCompactLayout, isNarrowViewport].some(Boolean);

  const hasComposerInput = String(currentInputValue ?? '').trim().length > 0;
  const shouldShowRecommendationPanel = [
    shouldRenderRecommendationPanel,
    !hasComposerInput,
    !recommendationPanelDismissed,
  ].every(Boolean);
  const handleRecommendationScenarioCardClick = React.useCallback((entry) => {
    if (typeof onScenarioCardClick === 'function') onScenarioCardClick(entry);
    focusComposerInputAfterPromptClick();
  }, [onScenarioCardClick]);
  const latestUnreadCue = React.useMemo(() => ({
    visible: latestUnreadVisible,
    label: firstTruthy(labels.latest_unread_label, '查看最新'),
    description: firstTruthy(labels.latest_unread_description, '有新内容生成，点击跳到最新位置。'),
    onReadLatest: () => setLatestScrollSignal((current) => current + 1),
  }), [
    latestUnreadVisible,
    labels.latest_unread_description,
    labels.latest_unread_label,
  ]);
  const resolvedComposerNode = React.useMemo(() => {
    if (!React.isValidElement(composerNode) || typeof composerNode.type === 'string') {
      return composerNode;
    }
    return React.cloneElement(composerNode, { latestUnreadCue });
  }, [composerNode, latestUnreadCue]);
  const handleRecommendationDismiss = React.useCallback(() => setRecommendationPanelDismissed(true), []);
  const recommendationPanelNode = React.useMemo(() => conditionalValue(
    [shouldShowRecommendationPanel, visibleScenarios.length > 0].every(Boolean),
    (
      <ChatWorkspaceSectionShell
        background={themeTokens.appBg}
        className="flare-workspace-recommendation-shell flare-staged-entry"
        contentMaxWidth={contentMaxWidth}
        padding={conditionalValue(shouldUseCompactRecommendationPanel, '4px 12px 4px', '5px 20px 5px')}
        style={{
          '--flare-entry-stage': 2,
          background: themeTokens.appBg,
        }}
      >
        <RecommendationActionStrip
          canExpandScenarios={canExpandScenarios}
          compact={shouldUseCompactRecommendationPanel}
          onDismiss={handleRecommendationDismiss}
          onScenarioCardClick={handleRecommendationScenarioCardClick}
          onToggleShowAllScenarios={onToggleShowAllScenarios}
          resolvedUILabels={resolvedUILabels}
          showAllScenarios={showAllScenarios}
          themeTokens={themeTokens}
          title={recommendationPanelTitle}
          visibleScenarios={visibleScenarios}
        />
      </ChatWorkspaceSectionShell>
    )
  ), [shouldShowRecommendationPanel, visibleScenarios, themeTokens, contentMaxWidth,
    shouldUseCompactRecommendationPanel, canExpandScenarios, handleRecommendationDismiss,
    handleRecommendationScenarioCardClick, onToggleShowAllScenarios, resolvedUILabels,
    showAllScenarios, recommendationPanelTitle]);
  // Keep recommendations in the existing virtualized scroll flow after a reply.
  const timelineRecommendationPanelNode = React.useMemo(() => conditionalValue(
    [!renderedTimeline.isEmpty, Boolean(recommendationPanelNode)].every(Boolean),
    <div
      className={conditionalValue(shouldUseCompactRecommendationPanel, 'flare-chat-mobile-recommendation', 'flare-chat-inline-recommendation')}
      style={{ background: themeTokens.appBg }}
    >
      {recommendationPanelNode}
    </div>
  ), [recommendationPanelNode, renderedTimeline.isEmpty, shouldUseCompactRecommendationPanel, themeTokens.appBg]);
  const mainContentNode = (
    <>
      {renderedTimeline.isEmpty && !streamError ? (
        <div
          style={{
            background: themeTokens.appBg,
            display: 'grid',
            flex: 1,
            height: '100%',
            minHeight: 0,
            overflow: 'auto',
            padding: shouldUseCompactRecommendationPanel
              ? 'calc(100px + env(safe-area-inset-top, 0px)) 0 var(--flare-mobile-timeline-bottom-space, calc(176px + env(safe-area-inset-bottom, 0px)))'
              : 0,
            placeItems: 'center',
          }}
        >
          <div
            style={{
              margin: '0 auto',
              maxWidth: contentMaxWidth,
              padding: 16,
              width: '100%',
            }}
          >
            <EmptyTimeline
              description={firstTruthy(labels.empty_state_description, '发送第一条消息后，这里会显示完整会话时间线。')}
              imageAlt={labels.empty_state_logo_alt ?? ''}
              imageUrl={labels.empty_state_logo_url ?? ''}
              logoLabel={labels.sidebar_logo_label}
              logoTag={labels.sidebar_logo_tag}
              themeTokens={themeTokens}
              title={firstTruthy(labels.empty_state_title, '开始新对话')}
            />
          </div>
        </div>
      ) : (
        <ChatTimeline
          activeModeKey={resolvedActiveModeKey}
          assistantName={assistantName}
          contentMaxWidth={contentMaxWidth}
          error={streamError}
          isEmpty={false}
          items={renderedTimeline.items}
          latestScrollSignal={latestScrollSignal}
          loading={streamLoading}
          messageWindow={messageWindow}
          onLatestUnreadChange={setLatestUnreadVisible}
          onLoadOlderMessages={onLoadOlderMessages}
          onMessageCapabilityAction={onScenarioCardClick}
          onRetry={onStreamRetry}
          onUICardAction={onUICardAction}
          onWorkflowOperationAction={onWorkflowOperationAction}
          registry={registry}
          recommendationPanelNode={timelineRecommendationPanelNode}
          skillProjection={skillProjection}
          themeTokens={themeTokens}
        />
      )}

      {conditionalValue(showWorkspaceDebugPanel, (
        <ChatWorkspaceSectionShell
          background={themeTokens.appBg}
          contentMaxWidth={contentMaxWidth}
          padding={conditionalValue(isCompactLayout, '8px 12px', '8px 20px')}
          style={{ borderTop: `1px solid ${themeTokens.divider}`, maxHeight: 260, overflow: 'auto' }}
        >
          <Text style={{ color: themeTokens.textSecondary }} type="secondary">
            Workspace Debug · mode={resolvedDevMode} · events={workspaceEventLog.length}
          </Text>
          <pre
            style={{
              background: themeTokens.surfaceBg,
              border: `1px solid ${themeTokens.surfaceBorder}`,
              borderRadius: 8,
              color: themeTokens.textPrimary,
              fontSize: 12,
              lineHeight: 1.5,
              margin: '8px 0 0',
              maxHeight: 200,
              overflow: 'auto',
              padding: 0,
            }}
          >
            {JSON.stringify({
              doc: workspaceDocSnapshot,
              events: workspaceEventLog.slice(-20),
            }, null, 2)}
          </pre>
        </ChatWorkspaceSectionShell>
      ))}

    </>
  );

  return (
    <ChatWorkspacePanelCoordinator
      canvasPanelNode={conditionalValue(canvasWorkspaceFullscreenMode, canvasPanelNode)}
      canvasWorkspaceFullscreenMode={canvasWorkspaceFullscreenMode}
      composerNode={null}
      fullscreenComposerNode={null}
      isCompactLayout={isCompactLayout}
      showCanvasPanel={showCanvasPanel}
    >
      <ChatWorkspaceConversationLayout
        canvasPanelNode={canvasPanelNode}
        canvasWorkspaceFullscreenMode={canvasWorkspaceFullscreenMode}
        composerNode={(
          <div className="flare-staged-entry" style={{ '--flare-entry-stage': 3 }}>
            {resolvedComposerNode}
          </div>
        )}
        background={themeTokens.appBg}
        isCompactLayout={isCompactLayout}
        mainContentNode={(
          <div
            className="flare-staged-entry"
            style={{ '--flare-entry-stage': 1, display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}
          >
            {mainContentNode}
          </div>
        )}
        onCanvasPanelClose={onCanvasPanelClose}
        recommendationPanelNode={conditionalValue(renderedTimeline.isEmpty, recommendationPanelNode)}
        showCanvasPanel={showCanvasPanel}
      />
    </ChatWorkspacePanelCoordinator>
  );
}
